import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import { Progress } from '../ui/progress';
import { Separator } from '../ui/separator';
import { Star, ThumbsUp, ThumbsDown, Smile, Frown, Meh, Loader2 } from 'lucide-react';
import { reportsAPI } from '../../services/api';

interface CSATAnalyticsProps {
  dateRange: {
    startDate: Date;
    endDate: Date;
  };
}

interface CSATData {
  average_rating: number;
  total_responses: number;
  rating_distribution: Record<string, number>;
  recent_feedback: Array<{
    rating: number;
    feedback: string;
    date: string;
    conversation_id: number;
  }>;
}

const StarRating: React.FC<{ rating: number; size?: 'small' | 'medium' }> = ({
  rating,
  size = 'medium'
}) => {
  const iconClass = size === 'small' ? 'h-3 w-3' : 'h-4 w-4';
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          className={`${iconClass} ${i <= rating ? 'fill-current text-muted-foreground' : 'text-muted-foreground/30'}`}
        />
      ))}
    </div>
  );
};

const RatingBar: React.FC<{ rating: number; count: number; total: number }> = ({
  rating,
  count,
  total
}) => {
  const percentage = total > 0 ? (count / total) * 100 : 0;
  return (
    <div className="flex items-center gap-3 mb-2">
      <div className="flex items-center gap-1 w-14">
        <span className="text-sm">{rating}</span>
        <Star className="h-3 w-3 fill-current text-muted-foreground" />
      </div>
      <Progress value={percentage} className="flex-1 h-2" />
      <span className="text-sm w-8 text-right">{count}</span>
      <span className="text-xs text-muted-foreground w-10 text-right">({percentage.toFixed(0)}%)</span>
    </div>
  );
};

const getSatisfactionLevel = (score: number) => {
  if (score >= 4.5) return { label: 'Excellent', Icon: Smile };
  if (score >= 4.0) return { label: 'Very Good', Icon: Smile };
  if (score >= 3.0) return { label: 'Good', Icon: Meh };
  if (score >= 2.0) return { label: 'Fair', Icon: Meh };
  return { label: 'Poor', Icon: Frown };
};

export const CSATAnalytics: React.FC<CSATAnalyticsProps> = ({ dateRange }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [csatData, setCSATData] = useState<CSATData | null>(null);

  useEffect(() => {
    fetchCSATData();
  }, [dateRange]);

  const fetchCSATData = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        start_date: dateRange.startDate.toISOString().split('T')[0],
        end_date: dateRange.endDate.toISOString().split('T')[0]
      };

      const data = await reportsAPI.getCSATReport(params);
      setCSATData(data);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch CSAT analytics');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center p-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  if (!csatData || csatData.total_responses === 0) {
    return (
      <Alert>
        <AlertDescription>
          No customer satisfaction data available for the selected period.
          Encourage customers to rate their experience to see analytics here.
        </AlertDescription>
      </Alert>
    );
  }

  const positiveRatings = (csatData.rating_distribution['4_star'] || 0) + (csatData.rating_distribution['5_star'] || 0);
  const negativeRatings = (csatData.rating_distribution['1_star'] || 0) + (csatData.rating_distribution['2_star'] || 0);

  const positivePercentage = csatData.total_responses > 0 ? (positiveRatings / csatData.total_responses) * 100 : 0;
  const negativePercentage = csatData.total_responses > 0 ? (negativeRatings / csatData.total_responses) * 100 : 0;

  const { label: satisfactionLabel, Icon: SatisfactionIcon } = getSatisfactionLevel(csatData.average_rating);
  const satisfactionPercentage = (csatData.average_rating / 5) * 100;

  const exportData = [
    {
      average_rating: csatData.average_rating,
      total_responses: csatData.total_responses,
      positive_percentage: positivePercentage,
      negative_percentage: negativePercentage
    },
    ...(csatData.recent_feedback || [])
  ];

  return (
    <div className="space-y-6">

      {/* Overall Satisfaction Summary */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
        {/* Satisfaction Score */}
        <Card className="bg-gradient-to-t from-primary/5 to-card shadow-sm">
          <CardHeader>
            <CardTitle className="text-sm font-medium text-center">Overall Satisfaction</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col items-center gap-3">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted">
                <SatisfactionIcon className="h-8 w-8 text-muted-foreground" />
              </div>
              <div className="text-4xl font-bold">{csatData.average_rating.toFixed(1)}</div>
              <StarRating rating={Math.round(csatData.average_rating)} />
              <Progress value={satisfactionPercentage} className="w-full h-2" />
              <Badge variant="secondary">{satisfactionLabel}</Badge>
            </div>
          </CardContent>
        </Card>

        {/* Positive / Negative */}
        <Card className="bg-gradient-to-t from-primary/5 to-card shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Positive Feedback</CardTitle>
            <ThumbsUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-4xl font-bold">{positivePercentage.toFixed(0)}%</div>
            <p className="text-xs text-muted-foreground mt-1">
              {positiveRatings} out of {csatData.total_responses} responses
            </p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-t from-primary/5 to-card shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Negative Feedback</CardTitle>
            <ThumbsDown className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-4xl font-bold">{negativePercentage.toFixed(0)}%</div>
            <p className="text-xs text-muted-foreground mt-1">
              {negativeRatings} out of {csatData.total_responses} responses
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Response counts */}
      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Responses</CardTitle>
            <Star className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{csatData.total_responses.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">Customer ratings received</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Average Rating</CardTitle>
            <Star className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <div className="text-2xl font-bold">{csatData.average_rating.toFixed(1)}</div>
              <Star className="h-5 w-5 fill-current text-muted-foreground" />
            </div>
            <div className="mt-1">
              <StarRating rating={Math.round(csatData.average_rating)} size="small" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Rating Distribution */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium">Rating Distribution</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-1 mt-2">
            {[5, 4, 3, 2, 1].map((rating) => {
              const count = csatData.rating_distribution[`${rating}_star`] || 0;
              return (
                <RatingBar
                  key={rating}
                  rating={rating}
                  count={count}
                  total={csatData.total_responses}
                />
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Recent Feedback */}
      {csatData.recent_feedback.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">Recent Customer Feedback</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {csatData.recent_feedback.map((feedback, index) => (
                <div key={index} className="rounded-lg border border-border bg-muted/30 p-3">
                  <div className="flex items-start justify-between mb-1">
                    <div className="space-y-1">
                      <StarRating rating={feedback.rating} size="small" />
                      <p className="text-xs text-muted-foreground">
                        Conversation #{feedback.conversation_id}
                      </p>
                    </div>
                    <span className="text-xs text-muted-foreground">{formatDate(feedback.date)}</span>
                  </div>
                  {feedback.feedback && (
                    <p className="text-sm italic text-muted-foreground mt-2">
                      "{feedback.feedback}"
                    </p>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
